import { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';

import Container from './Container';
import Reveal from '../../../components/Reveal';
import andy from './../../../assets/images/askAndy.png';

const TABS = [
  {
    key: 'explain',
    tab: 'Clear Explanations',
    badge: 'Andy Explains',
    title: 'Explains Anything Clearly',
    body: "When you're stuck, Andy breaks complex topics into simple explanations you can actually understand. Ask questions, get examples, and keep going until the topic finally makes sense.",
  },
  {
    key: 'notes',
    tab: 'Builds From Your Notes',
    badge: 'Andy Learns',
    title: 'Learns From Your Material',
    body: 'Upload your lecture notes, slides, or textbook chapters and Andy studies them with you — answering questions grounded in your own course content, not generic internet answers.',
  },
  {
    key: 'checks',
    tab: 'Checks Understanding',
    badge: 'Andy Checks',
    title: 'Makes Sure It Sticks',
    body: 'Andy quizzes you as you go, spots the gaps in your understanding, and revisits the tricky parts — so you walk into every exam genuinely ready, not just hoping.',
  },
  {
    key: 'available',
    tab: 'Always Available',
    badge: 'Andy 24/7',
    title: 'Here Whenever You Study',
    body: 'Late-night cramming or an early-morning review — Andy is online 24/7. No appointments, no waiting, just instant help the moment a question pops into your head.',
  },
];

/* how long each tab stays open — the underline fills over the same span */
const TAB_DURATION = 6000;

const MeetAndy = () => {
  const [active, setActive] = useState(0);
  const current = TABS[active];

  /* when the underline finishes filling, move to the next tab */
  useEffect(() => {
    const id = setTimeout(
      () => setActive((i) => (i + 1) % TABS.length),
      TAB_DURATION,
    );
    return () => clearTimeout(id);
  }, [active]);

  return (
    <section className="relative overflow-hidden py-20 lg:py-28">
      <div className="glow left-1/4 top-1/3 h-[420px] w-[560px] bg-accent/8" />

      <Container className="relative">
        {/* ghost heading */}
        <div className="pointer-events-none absolute -top-4 left-1/2 flex w-screen -translate-x-1/2 justify-center overflow-hidden">
          <span className="ghost-outline select-none whitespace-nowrap text-[17vw] leading-none">
            Meet <span className="ghost-solid">Andy</span>
          </span>
        </div>

        <div className="relative mx-auto max-w-5xl pt-[20vw]">
          {/* heading + robot */}
          <Reveal className="relative flex items-end justify-between gap-4">
            <h2 className="font-heading text-[2rem] font-bold leading-tight sm:text-[2.8rem] lg:text-[3.4rem]">
              <span className="text-accent-grad">Your AI study</span>{' '}
              <span className="text-white">Assistant.</span>
            </h2>
          </Reveal>

          {/* card: tabs + content */}
          <Reveal delay={120} className="relative mt-20 sm:mt-24 lg:mt-8">
            {/* Andy peeks from behind the card — the strip crops him at the
                card edge so his framed-off body never shows */}
            <div className="pointer-events-none absolute inset-x-0 bottom-full h-20 overflow-hidden sm:h-24 lg:h-44">
              <img
                src={andy}
                alt="Andy"
                className="absolute right-4 top-16 h-28 w-28 animate-float object-contain sm:h-32 sm:w-32 lg:right-8 lg:h-35 lg:w-35"
              />
            </div>

            <div className="card-dark relative overflow-hidden rounded-2xl">
              {/* tab row */}
              <div className="flex border-b border-white/8">
                {TABS.map((t, i) => (
                  <button
                    key={t.key}
                    onClick={() => setActive(i)}
                    className={`relative flex-1 px-3 py-4 text-center text-[0.8rem] font-medium transition-colors sm:text-[0.9rem] ${
                      i !== TABS.length - 1 ? 'border-r border-white/6' : ''
                    } ${active === i ? 'text-white' : 'text-white/50 hover:text-white/80'}`}
                  >
                    {t.tab}
                    <span className="absolute -bottom-px left-0 right-0 h-0.5 overflow-hidden rounded-full">
                      {active === i && (
                        <span
                          key={active}
                          style={{ animationDuration: `${TAB_DURATION}ms` }}
                          className="tab-progress block h-full w-full rounded-full bg-accent"
                        />
                      )}
                    </span>
                  </button>
                ))}
              </div>

              {/* content */}
              <div key={current.key} className="animate-fade-up p-6 md:p-8">
                <span className="inline-flex rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[0.7rem] font-medium text-accent">
                  {current.badge}
                </span>
                <h3 className="font-heading mt-4 text-[1.35rem] font-semibold text-white md:text-[1.6rem]">
                  {current.title}
                </h3>
                <p className="mt-3 max-w-3xl text-[0.95rem] leading-relaxed text-white/60">
                  {current.body}
                </p>
                <a
                  href="https://app.schoolhack.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary mt-6 px-5 py-2.5 text-[0.85rem]"
                >
                  Try now
                  <ArrowRight size={15} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export default MeetAndy;
